/**
 * Beem — Web Push subscribe/unsubscribe helper.
 * Usa el service worker para recibir notificaciones nativas incluso con la
 * app cerrada o el celular bloqueado.
 */
import { api } from "@/lib/api";

function urlBase64ToUint8Array(value) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(base64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function getPushPermission() {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

export async function getCurrentSubscription() {
  if (!isPushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/**
 * Pide permiso y suscribe al usuario a push. Debe llamarse desde un gesto
 * del usuario (click).
 */
export async function enablePush() {
  if (!isPushSupported()) throw new Error("Este navegador no soporta notificaciones push.");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("No diste permiso para notificaciones. Actívalo en la configuración del navegador.");
  }
  const reg = await navigator.serviceWorker.ready;
  const { data } = await api.get("/push/public-key");
  const publicKey = data?.publicKey;
  if (!publicKey) throw new Error("El servidor no tiene push configurado.");
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }
  await api.post("/push/subscribe", sub.toJSON());
  return sub;
}

export async function disablePush() {
  const sub = await getCurrentSubscription();
  if (!sub) return;
  try {
    await api.delete("/push/subscribe", { params: { endpoint: sub.endpoint } });
  } catch {}
  try {
    await sub.unsubscribe();
  } catch {}
}
